import { z } from "zod";
import { flag, int, isoDate, lenientArray, real, text } from "./common";

const seconds = z.number().nonnegative().transform(Math.trunc);

/**
 * `GET /me/plan`: the subscription and how much talk is left.
 *
 * Free is three minutes of conversation, once; `locked` means they are used
 * and there is no plan, so everything waits on the paywall. A subscriber has
 * `seconds_remaining: null` and an hour of voice a month. Anything unreadable
 * fails closed for a free account (locked, no voice) and never locks a
 * subscriber.
 */
export const userPlanSchema = z
  .object({
    tier: text("free"),
    active: flag(),
    renews: flag(),
    expires_at: isoDate(),
    locked: z.boolean().optional().catch(undefined),
    seconds_remaining: seconds.nullable().optional().catch(undefined),
    voice_seconds_remaining: seconds.catch(0),
    voice_seconds_per_month: seconds.nullable().catch(null),
  })
  .transform((plan) => ({
    ...plan,
    locked: plan.locked ?? !plan.active,
    seconds_remaining: plan.seconds_remaining === undefined ? (plan.active ? null : 0) : plan.seconds_remaining,
  }));
export type UserPlan = Readonly<z.output<typeof userPlanSchema>>;

/** "3 free minutes left", "45 minutes of voice left this month", … */
export function describeTalkTime(plan: UserPlan): string {
  if (plan.seconds_remaining === null) {
    const minutes = Math.ceil(plan.voice_seconds_remaining / 60);
    if (minutes <= 0) return "This month's voice is used. Typing is unlimited.";
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"} of voice left this month`;
  }
  const left = plan.seconds_remaining;
  if (left <= 0) return "Your free minutes are used";
  if (left < 60) return "Less than a minute left";
  const minutes = Math.ceil(left / 60);
  return `${minutes} free ${minutes === 1 ? "minute" : "minutes"} left`;
}

export const creditPackSchema = z.object({
  product_id: text(),
  price_usd: real(),
  credits: int(),
});
export type CreditPack = Readonly<z.output<typeof creditPackSchema>>;

/** `GET /catalog`: what each action costs and which packs exist. */
export const catalogSchema = z.object({
  actions: z.record(z.string(), int()).catch({}),
  packs: lenientArray(creditPackSchema),
});
export type Catalog = Readonly<z.output<typeof catalogSchema>>;

export const walletSchema = z.object({ balance: int() });

/** `DELETE /me`: whether the auth record went with the data. */
export const accountDeletionSchema = z.object({
  auth_record_deleted: flag(),
});
export type AccountDeletion = Readonly<z.output<typeof accountDeletionSchema>>;
